import React from 'react'
import { useTranslation } from "react-i18next";

import {
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Chip,
    Box,
    Checkbox,
    Divider,
    Autocomplete,
    TextField,
} from '@mui/material';

const CrmSelectField = (props) => {
    const { name, label, value, onChange, options, disabled, sx, required, isFilteringMode = false } = props
    const [t] = useTranslation('crm');

    // Use Autocomplete for options with more than 5 items (searchable)
    const useAutocomplete = options && options.length > 0;

    // Handle multi-selection value for filtering mode
    const normalizedValue = isFilteringMode ? (Array.isArray(value) ? value : (value ? [value] : [])) : value;
    
    // Handle Select All / Unselect All functionality
    const handleSelectAll = () => {
        const allValues = options?.map(option => option.value) || [];
        onChange({ target: { name, value: allValues } });
    };

    const handleUnselectAll = () => {
        onChange({ target: { name, value: [] } });
    };

    const isAllSelected = options && Array.isArray(normalizedValue) && normalizedValue.length === options.length;
    const isIndeterminate = Array.isArray(normalizedValue) && normalizedValue.length > 0 && normalizedValue.length < (options?.length || 0);

    // Handle change events, including Select All/Unselect All
    const handleChange = (event) => {
        const newValue = event.target.value;
        
        if (isFilteringMode && Array.isArray(newValue)) {
            // Check if Select All/Unselect All was clicked
            if (newValue.includes("__select_all__")) {
                if (isAllSelected) {
                    // Unselect all
                    handleUnselectAll();
                } else {
                    // Select all
                    handleSelectAll();
                }
                return;
            }
        }
        
        // Regular change handling
        onChange(event);
    };

    let optionArr = []
    if (options) {
        if (isFilteringMode) {
            // Add Select All / Unselect All options for filtering mode
            optionArr.push(
                <MenuItem 
                    key="select-all" 
                    value="__select_all__"
                    sx={{ 
                        direction: 'ltr', 
                        textAlign: 'right',
                        backgroundColor: '#f5f5f5',
                        fontWeight: 'bold',
                        minHeight: '36px',
                        height: '36px',
                        '&:hover': {
                            backgroundColor: '#eeeeee',
                        }
                    }}
                >
                    <Checkbox
                        checked={isAllSelected}
                        indeterminate={isIndeterminate}
                        size="small"
                        sx={{ marginRight: 1 }}
                    />
                    {isAllSelected ? t('Unselect All') : t('Select All')}
                </MenuItem>
            );
            optionArr.push(<Divider key="divider" />);
        }
        
        // Add regular options - EXACTLY like the original
        optionArr.push(...options.map(option => (
            <MenuItem 
                key={option.value} 
                value={option.value} 
                sx={{ 
                    direction: 'ltr', 
                    textAlign: 'right',
                    minHeight: '36px',
                    height: '36px',
                    // Gentler styling for selected items in multi-select mode
                    ...(isFilteringMode && Array.isArray(normalizedValue) && normalizedValue.includes(option.value) && {
                        backgroundColor: 'rgba(25, 118, 210, 0.12) !important',
                        '&:hover': {
                            backgroundColor: 'rgba(25, 118, 210, 0.24) !important',
                        },
                        '&.Mui-selected': {
                            backgroundColor: 'rgba(25, 118, 210, 0.12) !important',
                            '&:hover': {
                                backgroundColor: 'rgba(25, 118, 210, 0.24) !important',
                            }
                        }
                    })
                }}
            >
                {isFilteringMode && (
                    <Checkbox 
                        checked={Array.isArray(normalizedValue) && normalizedValue.includes(option.value)}
                        size="small"
                        sx={{ marginRight: 1 }}
                    />
                )}
                {option.label}
            </MenuItem>
        )));
    }

    // Custom render function for multi-select chips
    const renderValue = (selected) => {
        if (!isFilteringMode || !Array.isArray(selected)) {
            return selected;
        }
        
        return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => {
                    const option = options?.find(opt => opt.value === value);
                    return (
                        <Chip 
                            key={value} 
                            label={option?.label || value} 
                            size="small"
                            sx={{ height: 20, fontSize: '0.75rem' }}
                        />
                    );
                })}
            </Box>
        );
    };

    // Use Autocomplete for better search experience when many options
    if (useAutocomplete) {
        // Add "Select All" option in filtering mode
        const autocompleteOptions = isFilteringMode
            ? [{ value: '__select_all__', label: isAllSelected ? t('Unselect All') : t('Select All'), isSelectAll: true }, ...(options || [])]
            : (options || []);

        return (
            <Autocomplete
                fullWidth
                options={autocompleteOptions}
                getOptionLabel={(option) => option.label || option.key || ''}
                value={isFilteringMode
                    ? (options?.filter(opt => normalizedValue.includes(opt.value)) || [])
                    : (options?.find(opt => opt.value === value) || null)
                }
                multiple={isFilteringMode}
                onChange={(event, newValue) => {
                    if (isFilteringMode) {
                        // Check if Select All was clicked
                        const selectAllClicked = newValue?.some(item => item.value === '__select_all__');

                        if (selectAllClicked) {
                            // Toggle select all
                            if (isAllSelected) {
                                handleUnselectAll();
                            } else {
                                handleSelectAll();
                            }
                        } else {
                            // Regular multi-select
                            const values = newValue ? newValue.map(item => item.value).filter(v => v !== '__select_all__') : [];
                            onChange({ target: { name, value: values } });
                        }
                    } else {
                        // Single select mode
                        onChange({ target: { name, value: newValue?.value || '' } });
                    }
                }}
                disabled={disabled}
                disableCloseOnSelect={isFilteringMode}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={label}
                        required={required}
                        size="small"
                    />
                )}
                renderOption={(props, option, { selected }) => {
                    // Special handling for "Select All" option
                    if (option.isSelectAll) {
                        const { key, ...otherProps } = props;
                        return (
                            <>
                                <Box
                                    component="li"
                                    key={key}
                                    {...otherProps}
                                    sx={{
                                        direction: 'ltr',
                                        textAlign: 'right',
                                        backgroundColor: '#f5f5f5',
                                        fontWeight: 'bold',
                                        minHeight: '36px !important',
                                        height: '36px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        '&:hover': {
                                            backgroundColor: '#eeeeee',
                                        }
                                    }}
                                >
                                    <Checkbox
                                        checked={isAllSelected}
                                        indeterminate={isIndeterminate}
                                        size="small"
                                        sx={{ marginRight: 1 }}
                                    />
                                    {option.label}
                                </Box>
                                <Divider key={`${key}-divider`} />
                            </>
                        );
                    }

                    // Regular options
                    const isSelected = isFilteringMode
                        ? (Array.isArray(normalizedValue) && normalizedValue.includes(option.value))
                        : selected;

                    const { key, ...otherProps } = props;
                    return (
                        <Box
                            component="li"
                            key={key}
                            {...otherProps}
                            sx={{
                                direction: 'ltr',
                                textAlign: 'right',
                                minHeight: '36px !important',
                                height: '36px',
                                display: 'flex',
                                alignItems: 'center',
                            }}
                        >
                            {isFilteringMode && (
                                <Checkbox
                                    checked={isSelected}
                                    size="small"
                                    sx={{ marginRight: 1 }}
                                />
                            )}
                            {option.label}
                        </Box>
                    );
                }}
                renderTags={(tagValue, getTagProps) =>
                    tagValue.map((option, index) => (
                        <Chip
                            label={option.label}
                            {...getTagProps({ index })}
                            size="small"
                            sx={{ height: 20, fontSize: '0.75rem' }}
                        />
                    ))
                }
                ListboxProps={{
                    sx: {
                        maxHeight: 400,
                        '& .MuiAutocomplete-option': {
                            direction: 'ltr',
                            textAlign: 'right',
                        }
                    }
                }}
                sx={{
                    mt: sx?.mt,
                    '& .MuiOutlinedInput-root': {
                        height: sx?.height
                    }
                }}
            />
        );
    }

    // Regular Select for small option lists
    return (
        <FormControl fullWidth sx={{
            mt: sx?.mt,
            height: sx?.height ? sx?.height - 5 : undefined
        }}
            required={required}
        >
            <InputLabel>{label}</InputLabel>
            <Select
                name={name}
                value={normalizedValue}
                onChange={handleChange}
                label={label}
                size="small"
                disabled={disabled}
                multiple={isFilteringMode}
                renderValue={isFilteringMode ? renderValue : undefined}
                sx={{
                    height: sx?.height
                }}
            >
                {optionArr}
            </Select>
        </FormControl>
    )
}

export default CrmSelectField